import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { Card } from 'primeng/card'
import { TableModule } from 'primeng/table'
import { DropdownModule } from 'primeng/dropdown'
import { CalendarModule } from 'primeng/calendar'
import { SelectButtonModule } from 'primeng/selectbutton'
import { ButtonModule } from 'primeng/button'
import { OptimizationService } from '../../services/optimization.service'
import { FormsModule } from '@angular/forms'
import { Tag } from 'primeng/tag'
import { DatePipe, NgIf } from '@angular/common'

@Component({
  selector: 'app-optimized-list',
  standalone: true,
  imports: [
    Card,
    TableModule,
    DropdownModule,
    CalendarModule,
    SelectButtonModule,
    ButtonModule,
    FormsModule,
    Tag,
    NgIf,
    DatePipe,
  ],
  templateUrl: './optimized-list.component.html',
  styleUrl: './optimized-list.component.scss',
})
export class OptimizedListComponent implements OnInit {
  optimizationResults: OptimizationResult[] = []
  optimizations: any[] = []

  technicians: any[] = []
  filteredResults: OptimizationResult[] = []

  // Pagination
  currentPage: number = 1
  totalRecords: number = 0
  rowsPerPage: number = 15

  // Filtres
  dateFrom?: Date
  dateTo?: Date
  selectedTechnician?: number
  optimizedOnly: boolean | null = null

  // Tri
  sortField: string = 'distance_saved_km'
  sortOrder: number = -1 // -1 pour décroissant, 1 pour croissant

  optimizationStatusOptions = [
    { label: 'Tous', value: null },
    { label: 'Optimisé', value: true },
    { label: 'Déjà optimal', value: false },
  ]

  constructor(
    private router: Router,
    private optimizationService: OptimizationService
  ) {}

  ngOnInit(): void {
    // Charger les techniciens dès le début
    this.loadTechnicians()
  }

  onLazyLoad(event: any) {
    console.log('Lazy load event:', event)
    this.currentPage = Math.floor(event.first / event.rows) + 1
    this.rowsPerPage = event.rows

    // Capturer les informations de tri
    if (event.sortField) {
      this.sortField = event.sortField
      this.sortOrder = event.sortOrder
    }

    this.loadOptimizations()
  }

  loadOptimizations() {
    const params: any = {
      page: this.currentPage,
    }

    // Ajouter les filtres aux paramètres si ils sont définis
    if (this.dateFrom) {
      params.date_from = this.dateFrom.toISOString().split('T')[0]
    }
    if (this.dateTo) {
      params.date_to = this.dateTo.toISOString().split('T')[0]
    }
    if (this.selectedTechnician) {
      params.technician_id = this.selectedTechnician
    }
    if (this.optimizedOnly !== null) {
      params.optimized_only = this.optimizedOnly
    }

    // Ajouter les paramètres de tri
    if (this.sortField) {
      params.sort_field = this.sortField
      params.sort_order = this.sortOrder === 1 ? 'asc' : 'desc'
    }

    console.log('Paramètres envoyés:', params)

    this.optimizationService.getOptimizedRoutes(params).subscribe({
      next: (response: any) => {
        this.optimizations = response.data
        this.totalRecords = response.meta.total
        this.currentPage = response.meta.current_page
        this.rowsPerPage = response.meta.per_page
        this.optimizationResults = this.optimizations.map((item: any) => ({
          technician: item.technician.name,
          technician_id: item.technician.id,
          date: item.analysis_date,
          gain: parseFloat(item.distance_saved_km),
          timeSaved: parseFloat(item.duration_saved_min),
          distancePercentSaved: parseFloat(item.distance_percent_saved),
          timePercentSaved: parseFloat(item.time_percent_saved),
          interventionsCount: item.interventions_count,
          isOptimized: item.is_optimized_better,
        }))
        this.filteredResults = this.optimizationResults
      },
      error: (error) => {
        console.error('Erreur lors du chargement des optimisations:', error)
      },
    })
  }

  loadTechnicians() {
    // Charger tous les techniciens pour le dropdown (sans pagination)
    this.optimizationService
      .getOptimizedRoutes({ all_technicians: true })
      .subscribe({
        next: (response: any) => {
          const allOptimizations = response.data || response
          this.technicians = Array.from(
            new Set(
              allOptimizations.map((item: any) =>
                JSON.stringify({
                  id: item.technician.id,
                  name: item.technician.name,
                })
              )
            )
          ).map((str: unknown) => JSON.parse(str as string))
        },
        error: (error) => {
          console.error('Erreur lors du chargement des techniciens:', error)
        },
      })
  }

  applyFilters() {
    this.currentPage = 1 // Reset à la première page lors de l'application des filtres
    this.loadOptimizations()
  }

  goToCheck(result: OptimizationResult) {
    const queryParams = new URLSearchParams({
      userId: result.technician_id.toString(),
      user: result.technician,
      date: result.date,
    })
    const url = `/tour-check?${queryParams.toString()}`
    window.open(url, '_blank')
  }

  openTourChecker() {
    window.open('/tour-check', '_blank')
  }
}

interface OptimizationResult {
  technician: string
  technician_id: number
  date: string
  gain: number
  timeSaved: number
  distancePercentSaved: number
  timePercentSaved: number
  interventionsCount: number
  isOptimized: boolean
}
